import { useMemo, useState } from "react"
import { useSearchParams, Link } from "react-router-dom"
import { useQuery } from "@tanstack/react-query"
import { Map, ChevronLeft } from "lucide-react"
import { api } from "@/app/api/client"
import { useClientTheme } from "@/app/components/ThemeContext"
import { KPICard } from "@/app/components/KPICard"
import { PageHeader } from "@/app/components/PageHeader"
import { SectionCard } from "@/app/components/SectionCard"
import { SectionModal } from "@/app/components/SectionModal"
import type { InventoryRow } from "@/app/components/InventoryTable"
import { TownshipEmptyState } from "./TownshipEmptyState"
import { TownshipSelector } from "./TownshipSelector"

const PLSS_GRID = [
  [6, 5, 4, 3, 2, 1],
  [7, 8, 9, 10, 11, 12],
  [18, 17, 16, 15, 14, 13],
  [19, 20, 21, 22, 23, 24],
  [30, 29, 28, 27, 26, 25],
  [31, 32, 33, 34, 35, 36],
] as const

interface OrriEntry {
  id: string
  str: string
  township_range: string
  orri: string
  net_acres_burdened: string
  mineral_transaction: { id: string; tx_number: string; transaction_date: string }
}

interface InventoryResponse {
  data: InventoryRow[]
  meta: { total_count: number; page: number; per_page: number; total_pages: number }
}

interface OrriResponse {
  data: OrriEntry[]
  meta: { total_count: number; page: number; per_page: number; total_pages: number }
}

export function TownshipPage() {
  const { theme } = useClientTheme()
  const [searchParams, setSearchParams] = useSearchParams()
  const selectedTR = searchParams.get("tr") ?? ""
  const fromInventory = searchParams.get("from") === "inventory"

  const [activeSection, setActiveSection] = useState<InventoryRow | null>(null)

  const { data: invData, isLoading: invLoading } = useQuery<InventoryResponse>({
    queryKey: ["inventories", "all"],
    queryFn: () => api.get<InventoryResponse>("/inventories?per_page=200"),
  })

  const allInventories = invData?.data ?? []

  const trOptions = useMemo(
    () => Array.from(new Set(allInventories.map((r) => r.township_range).filter(Boolean))).sort(),
    [allInventories]
  )

  const holdings = useMemo(
    () => (selectedTR ? allInventories.filter((r) => r.township_range === selectedTR) : []),
    [allInventories, selectedTR]
  )

  const { data: orriData } = useQuery<OrriResponse>({
    queryKey: ["orri_inventories", "township", selectedTR],
    queryFn: () =>
      api.get<OrriResponse>(
        `/orri_inventories?township_range=${encodeURIComponent(selectedTR)}&per_page=200`
      ),
    enabled: !!selectedTR,
  })

  const orriEntries = orriData?.data ?? []

  const orriByStr = useMemo(() => {
    const acc: Record<string, number> = {}
    orriEntries.forEach((entry) => {
      acc[entry.str] = (acc[entry.str] ?? 0) + Number(entry.orri)
    })
    return acc
  }, [orriEntries])

  const holdingsBySection = useMemo(() => {
    const acc: Record<number, InventoryRow> = {}
    holdings.forEach((h) => {
      if (h.section !== null) acc[h.section] = h
    })
    return acc
  }, [holdings])

  const ownedCount = holdings.length
  const totalAcres = holdings.reduce((s, r) => s + Number(r.net_acres), 0)
  const avgNri = ownedCount
    ? holdings.reduce((s, r) => s + Number(r.weighted_nri), 0) / ownedCount
    : 0
  const county = holdings[0]?.county ?? "—"

  const handleTRChange = (value: string) => {
    const next = new URLSearchParams(searchParams.toString())
    if (value) next.set("tr", value)
    else next.delete("tr")
    setSearchParams(next, { replace: true })
    setActiveSection(null)
  }

  const activeTotalOrri = activeSection ? (orriByStr[activeSection.str] ?? null) : null

  return (
    <div className="p-4 sm:px-6 sm:pt-5 sm:pb-6">
      <div>
        {fromInventory && (
          <Link
            to="/inventory"
            className="inline-flex items-center gap-1.5 mb-4"
            style={{
              fontFamily: "var(--font-ui)",
              fontSize: "12px",
              color: theme.textMuted,
              textDecoration: "none",
            }}
          >
            <ChevronLeft size={13} />
            Back to Master Inventory
          </Link>
        )}

        <PageHeader
          title="Township-Range"
          subtitle="Section grid by Township-Range — click an owned section for detail"
          theme={theme}
          icon={<Map size={17} strokeWidth={1.75} />}
        />

        <TownshipSelector
          trOptions={trOptions}
          selectedTR={selectedTR}
          invLoading={invLoading}
          onChange={handleTRChange}
          theme={theme}
        />

        {!selectedTR ? (
          <TownshipEmptyState invLoading={invLoading} trCount={trOptions.length} theme={theme} />
        ) : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 mb-4">
              <KPICard
                label="Owned Sections"
                value={ownedCount}
                trend="of 36 sections"
                accentColor={theme.kpiAccents[0]}
                icon={<Map size={16} strokeWidth={1.5} />}
              />
              <KPICard
                label="Total Net Acres"
                value={totalAcres.toFixed(2)}
                trend="In this township"
                accentColor={theme.kpiAccents[1]}
                icon={<Map size={16} strokeWidth={1.5} />}
              />
              <KPICard
                label="Avg NRI"
                value={`${(avgNri * 100).toFixed(2)}%`}
                trend="Across owned sections"
                accentColor={theme.kpiAccents[2]}
                icon={<Map size={16} strokeWidth={1.5} />}
              />
              <KPICard
                label="County"
                value={county}
                trend={selectedTR}
                accentColor={theme.kpiAccents[3]}
                icon={<Map size={16} strokeWidth={1.5} />}
              />
            </div>

            <div className="mb-3">
              <h2
                style={{
                  fontFamily: "var(--font-ui)",
                  fontSize: "14px",
                  fontWeight: 600,
                  color: theme.textPrimary,
                }}
              >
                {selectedTR} — Section Grid
              </h2>
              <p
                style={{
                  fontFamily: "var(--font-ui)",
                  fontSize: "12px",
                  color: theme.textMuted,
                  marginTop: "1px",
                }}
              >
                Standard PLSS layout · {ownedCount} section{ownedCount !== 1 ? "s" : ""} with
                holdings · click a card to view transaction history
              </p>
            </div>

            {/* Horizontal scroll on mobile keeps the PLSS 6×6 layout legible. */}
            <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
              <div className="grid grid-cols-6 gap-1 min-w-[540px]">
                {PLSS_GRID.flat().map((sectionNumber) => {
                  const holding = holdingsBySection[sectionNumber] ?? null
                  const orriVal = holding ? (orriByStr[holding.str] ?? null) : null
                  return (
                    <SectionCard
                      key={sectionNumber}
                      sectionNumber={sectionNumber}
                      holding={holding}
                      totalOrri={orriVal}
                      onClick={holding ? () => setActiveSection(holding) : undefined}
                    />
                  )
                })}
              </div>
            </div>
          </>
        )}
      </div>

      {activeSection && (
        <SectionModal
          str={activeSection.str}
          township_range={activeSection.township_range}
          holding={activeSection}
          totalOrri={activeTotalOrri}
          onClose={() => setActiveSection(null)}
        />
      )}
    </div>
  )
}
